import {BehaviorSubject} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {uiMsg$} from '@/ui';
import {toWsMsg$, wsMsg$} from '@/ws';
import {wsLogin$} from '@/user/auth-ws';
import type {ws, UserInfo, SubUserInfo, FUInitUserData} from '@/types';

export interface FUSubUsersSubscribe {
  type: 'sub-users-subscribe';
  users: {
    main: UserInfo;
    subs: SubUserInfo[];
  };
}

export const mainUserId$ = new BehaviorSubject<number>(0);

export const subUsers$ = new BehaviorSubject<SubUserInfo[]>([]);
export const subUserIds$ = subUsers$.pipe(map((u) => u.map((u) => u.userId)));
export const contractBotTradeUserIds$ = subUsers$.pipe(
  map((u) => u.filter(isContractBotTradeUser).map((u) => u.userId))
);
export const copyTradeUserIds$ = subUsers$.pipe(map((u) => u.filter(isCopyTradeUser).map((u) => u.userId)));

// 主要给资产统计使用，因此合约空投仓位类型sub user不需要包含在内
export const contractSubUserIds$ = subUsers$.pipe(
  map((u) => u.filter((u) => isContractBotTradeUser(u) || isCopyTradeUser(u)).map((u) => u.userId))
);
export const spotBotTradeSubUserIds$ = subUsers$.pipe(map((u) => u.filter(isSpotBotTradeUser).map((u) => u.userId)));

export const contractAirdropPosSubUserIds$ = subUsers$.pipe(
  map((u) => u.filter(isContractAirdropPositionUser).map((u) => u.userId))
);

uiMsg$.pipe(filter((o) => o.type === 'init-user-data')).subscribe((o: FUInitUserData) => {
  mainUserId$.next(o.data.userId);
  subUsers$.next((o.data.subUsers || []).filter(validSubUsers));
});

function isCopyTradeUser(user: SubUserInfo) {
  return user.userBizType === 'CT_FOLLOWER';
}

// CONTRACT_FUNDING_FEE 是一个特殊的类型，需要分别订阅futures/spot的balance数据
function isContractBotTradeUser(user: SubUserInfo) {
  return (
    user.userBizType === 'BOT_TRADE_ACCOUNT' &&
    ['CONTRACT', 'CONTRACT_MARTINGALE', 'CONTRACT_SIGNAL', 'CONTRACT_FUNDING_FEE'].includes(user.botType)
  );
}

function isSpotBotTradeUser(user: SubUserInfo) {
  return user.userBizType === 'BOT_TRADE_ACCOUNT' && ['CONTRACT_FUNDING_FEE', 'SPOT'].includes(user.botType);
}

function isContractAirdropPositionUser(user: SubUserInfo) {
  return user.userBizType === 'BOT_TRADE_ACCOUNT' && user.botType == 'CONTRACT_EQUITY';
}

// account
wsLogin$.subscribe(() => toWsMsg$.next({method: 'service.subscribe', params: ['service.account']}));

export const userInfoMsg$ = wsMsg$.pipe(
  filter((o: ws.UserMsg) => o.msgType === 'account.user.common' && o.payload && !!o.payload[0]),
  map((o) => o.payload[0])
);

userInfoMsg$
  .pipe(
    filter((o) => o.bizType === null || o.bizType === 'ACCOUNT'),
    map((o) => o.content),
    filter(Boolean)
  )
  .subscribe((o) => {
    if (o.userId) {
      mainUserId$.next(o.userId);
    }
    if (o.subUsers) {
      // 要过滤业务
      subUsers$.next((o.subUsers || []).filter((user) => validSubUsers(user)));
    }
  });

function validSubUsers(subUser: SubUserInfo) {
  return (
    isCopyTradeUser(subUser) ||
    isContractBotTradeUser(subUser) ||
    isContractAirdropPositionUser(subUser) ||
    isSpotBotTradeUser(subUser)
  );
}
